import { useEffect, useState, useCallback } from "react";
import { doc, onSnapshot, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";

/**
 * Site-wide tester program toggle from siteConfiguration/main.
 * Works the same way as useBetaMode — controls whether the tester program
 * is accepting new testers and showing tester UI.
 *
 * When disabled:
 * - Existing testers keep their data (courses, progress, feedback history)
 * - Tester-specific UI (feedback widget, NDA gates) stops showing
 * - New tester applications are not accepted
 *
 * Returns { testerProgramActive, loading, toggleTesterProgram }.
 */
export default function useTesterProgramMode() {
  const { currentUser } = useAuth();
  const [testerProgramActive, setTesterProgramActive] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ref = doc(db, "siteConfiguration", "main");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setTesterProgramActive(!!snap.data().testerProgramActive);
        }
        setLoading(false);
      },
      (err) => {
        console.warn("Tester program mode listener error:", err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const toggleTesterProgram = useCallback(
    async (newValue) => {
      const ref = doc(db, "siteConfiguration", "main");
      await setDoc(
        ref,
        {
          testerProgramActive: newValue,
          testerProgramToggledBy: currentUser?.email || "unknown",
          testerProgramToggledAt: Timestamp.now(),
        },
        { merge: true }
      );
    },
    [currentUser?.email]
  );

  return { testerProgramActive, loading, toggleTesterProgram };
}
